// ============================================================================
// Subcontractor capability derivation (2026-07-10).
//
// Capability is NOT a stored column — it is DERIVED from the free-text
// service_type already on service_assignments and projects. This single helper
// is the one place the mapping lives so the landing card grid, the detail-page
// header/tags, and the filter chips all agree.
//
// Rules are evaluated IN ORDER so specialty words win before the broad
// Floor / Cleaning catch-alls (e.g. "carpet" must resolve to Carpet, never
// Floor). A raw service_type contributes every capability whose keyword it
// matches — a value like "Window & Carpet Cleaning" yields BOTH Windows and
// Carpet. Unmatched values fall through to "Other" (reported for cleanup; we
// do NOT invent a category).
//
// 2026-07-10 (Ben): added Snow Removal. It is a distinct, significant project
// type — NOT the same as Grounds — so it gets its own capability and is placed
// above Grounds so snow/plow/de-ice work never collapses into landscaping.
//
// 2026-08-18 (Run A): EXTENDED, not replaced, to read the controlled
// service_catalog.service_code carried by project_services. A code is checked
// against SERVICE_CODE_CAPABILITIES FIRST and, on a hit, the keyword rules are
// skipped entirely. The keyword rules and their order are untouched, because
// they still have to fold ~77 legacy free-text service_type_raw values and that
// ordering is load-bearing ("carpet" before "floor", snow before grounds).
//
// The explicit map is not redundant with the keywords: lowercased codes like
// mow, grout and weed_shrub match NO keyword and would silently fall to "Other",
// quietly shrinking a sub's capability set the moment its work was recorded
// against the new vocabulary instead of the old string.
// ============================================================================

export type Capability =
  | 'Carpet'
  | 'Windows'
  | 'Snow Removal'
  | 'Grounds'
  | 'Floor'
  | 'Day Porter'
  | 'Management'
  | 'Cleaning'
  | 'Other';

interface CapabilityRule {
  capability: Exclude<Capability, 'Other'>;
  // Matched case-insensitively as substrings against the raw service_type.
  keywords: string[];
}

// Order matters — specialty categories first, broad catch-alls last.
export const CAPABILITY_RULES: CapabilityRule[] = [
  // Carpet STANDS ALONE — it is not Floor.
  { capability: 'Carpet', keywords: ['carpet'] },
  { capability: 'Windows', keywords: ['window'] },
  // Snow Removal — distinct from Grounds; evaluated before it so snow work
  // never reads as landscaping. Keywords cover snow / plowing / de-icing /
  // ice-melt / salting.
  { capability: 'Snow Removal', keywords: ['snow', 'plow', 'de-ice', 'deic', 'ice melt', 'ice-melt', 'salting'] },
  { capability: 'Grounds', keywords: ['lawn', 'landscap', 'grounds', 'trash'] },
  // All hard-floor work EXCEPT carpet (carpet already claimed above).
  { capability: 'Floor', keywords: ['strip', 'wax', 'burnish', 'scrub', 'floor'] },
  { capability: 'Day Porter', keywords: ['porter'] },
  { capability: 'Management', keywords: ['management'] },
  {
    capability: 'Cleaning',
    keywords: ['janitor', 'night', 'deep', 'terminal', 'clean', 'common', 'construction', 'special'],
  },
];

// Controlled service_code -> capability. One code means exactly one capability;
// a project that spans two capabilities has two service lines, so the union
// happens naturally in deriveCapabilities() rather than inside a single value.
export const SERVICE_CODE_CAPABILITIES: Record<string, Exclude<Capability, 'Other'>> = {
  DEEP_CLEAN: 'Cleaning',
  WINDOW_GLASS: 'Windows',
  CONSTRUCTION: 'Cleaning',
  SPECIAL_CLEAN: 'Cleaning',
  STRIP_WAX: 'Floor',
  SCRUB_RECOAT: 'Floor',
  AUTO_SCRUB: 'Floor',
  BURNISH: 'Floor',
  GROUT: 'Floor',
  CARPET_CLEAN: 'Carpet',
  MOW: 'Grounds',
  WEED_SHRUB: 'Grounds',
  MULCH_EDGE: 'Grounds',
  TRASH: 'Grounds',
  SNOW_PUSH: 'Snow Removal',
  SALT_DEICE: 'Snow Removal',
  PRESSURE_WASH: 'Grounds',
};

/**
 * Map a controlled service_catalog code to its capability. Returns [] for an
 * empty input and ['Other'] for the OTHER escape hatch or any code not in the
 * map — deliberately, so a newly seeded code shows up as unclassified rather
 * than being guessed into the wrong bucket.
 */
export function capabilitiesForServiceCode(serviceCode: string | null | undefined): Capability[] {
  const code = (serviceCode ?? '').trim().toUpperCase();
  if (!code) return [];
  const hit = SERVICE_CODE_CAPABILITIES[code];
  return hit ? [hit] : ['Other'];
}

/** True when the value looks like a controlled catalog code rather than legacy free text. */
function isServiceCode(v: string): boolean {
  return Object.prototype.hasOwnProperty.call(SERVICE_CODE_CAPABILITIES, v.toUpperCase())
    || v.toUpperCase() === 'OTHER';
}

// Stable display / sort order for chips and tags.
export const CAPABILITY_ORDER: Capability[] = [
  'Cleaning',
  'Floor',
  'Carpet',
  'Windows',
  'Grounds',
  'Snow Removal',
  'Day Porter',
  'Management',
  'Other',
];

/**
 * Map a single value to the capabilities it implies. Accepts EITHER a controlled
 * service_catalog code (checked first, exact) OR a legacy free-text
 * service_type_raw string (folded by the ordered keyword rules below).
 */
export function capabilitiesForServiceType(serviceType: string | null | undefined): Capability[] {
  const raw = (serviceType ?? '').trim();
  if (!raw) return [];
  if (isServiceCode(raw)) return capabilitiesForServiceCode(raw);
  const s = raw.toLowerCase();
  const hits = CAPABILITY_RULES.filter((r) => r.keywords.some((k) => s.includes(k))).map(
    (r) => r.capability,
  );
  return hits.length > 0 ? [...new Set(hits)] : ['Other'];
}

/**
 * Derive the distinct, display-ordered capability set for a subcontractor from
 * the union of its values (Active assignments ∪ projects). Members may be
 * controlled service_codes, legacy free-text service_type strings, or a mix —
 * during and after the backfill a sub will legitimately have both.
 */
export function deriveCapabilities(serviceTypes: Array<string | null | undefined>): Capability[] {
  const set = new Set<Capability>();
  for (const st of serviceTypes) {
    for (const cap of capabilitiesForServiceType(st)) set.add(cap);
  }
  return CAPABILITY_ORDER.filter((c) => set.has(c));
}

/** Values that matched NOTHING — surfaced for later cleanup. */
export function unmatchedServiceTypes(serviceTypes: Array<string | null | undefined>): string[] {
  const out = new Set<string>();
  for (const st of serviceTypes) {
    const s = (st ?? '').trim();
    if (!s) continue;
    // A controlled code in the map is matched by definition; OTHER is matched
    // as the deliberate escape hatch, not as a cleanup candidate.
    if (isServiceCode(s)) continue;
    const matched = CAPABILITY_RULES.some((r) => r.keywords.some((k) => s.toLowerCase().includes(k)));
    if (!matched) out.add(s);
  }
  return [...out];
}
